import { useAppDispatch } from '_store';
import { DialogType } from '_store/application/types';
import denmsReducer from '_store/denm/denmsReducers';
import { DenmSourceFilter } from '_store/denm/denmsTypes';
import { causesSelector, loadingSelector, subCausesSelector } from '_store/denm/selectors';
import { defaultIcon, useDenmsForRoadSegment } from '_store/denm/utils';
import { selectedRoadSegmentIdsSelector } from '_store/roads/selectors';
import { useLocalization } from '@fluent/react';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isFuture } from 'date-fns';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useRolePermissions } from '../../../hooks/useRolePermissions';
import { iconForCause } from '../../icons/utils/denm';
import CardDetails from '../Denm/DenmList/Card/Details';
import EventLayout from './EventLayout';

interface DenmLayoutProps {
  id: string;
  onUpdateDenm?: (idExpanded: string) => void;
  onCloneDenm?: (idExpanded: string) => void;
}

const DenmLayout: FC<DenmLayoutProps> = ({ id, onUpdateDenm, onCloneDenm }) => {
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();
  const { canWriteDenm, blockedActionMode } = useRolePermissions();

  const causes = useSelector(causesSelector);
  const subCauses = useSelector(subCausesSelector);
  const selectedRoadSegmentIds: string[] = useSelector(selectedRoadSegmentIdsSelector);
  const events = useDenmsForRoadSegment(selectedRoadSegmentIds);
  const isDenmLoading = useSelector(loadingSelector);
  const [requestedById, setRequestedById] = useState(false);
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  const onDeleteDenm = () => {
    dispatch(denmsReducer.actions.deleteDENM(id));
  };

  const onDisableDenm = () => {
    dispatch(denmsReducer.actions.disableDENM({ ids: [id], disable: true }));
  };

  const onEnableDenm = () => {
    dispatch(denmsReducer.actions.disableDENM({ ids: [id], disable: false }));
  };

  const event = events.find((denm) => denm.id === id || denm.datexiiId === id);

  useEffect(() => {
    setRequestedById(false);
  }, [id]);

  useEffect(() => {
    if (!event && !isDenmLoading && !requestedById) {
      setRequestedById(true);
      dispatch(denmsReducer.actions.denmRequest({ id }));
    }
  }, [dispatch, event, id, isDenmLoading, requestedById]);

  if (isDenmLoading || (!event && !requestedById)) {
    return <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: 10 }} />;
  }

  if (!event) {
    return (
      <EventLayout
        type={DialogType.DENM}
        active={false}
        disabled={false}
        header={l10n.getString('detail-denm-missing-header')}
        auditResourceId={id}
      >
        {l10n.getString('detail-denm-missing-description-0')}
        <br />
        <strong>{id}</strong>
        <br />
        {l10n.getString('detail-denm-missing-description-1')}
      </EventLayout>
    );
  }

  const subCause = subCauses.find((sc) => sc.id === event?.subCauseId);
  const cause = subCause && causes.find((c) => c.id === subCause.causeId);

  return (
    <EventLayout
      type={DialogType.DENM}
      active={isFuture(new Date(event.expiresAt))}
      disabled={event.disabled && new Date(event.expiresAt) > new Date()}
      header={cause?.description ?? l10n.getString('detail-denm-default-event')}
      date={new Date(event.createdAt)}
      onDelete={event.source === DenmSourceFilter.EMERAS && onDeleteDenm}
      onDisable={event.source === DenmSourceFilter.EMERAS && onDisableDenm}
      onEnable={event.source === DenmSourceFilter.EMERAS && onEnableDenm}
      causeIcon={iconForCause(cause, subCause) ?? defaultIcon}
      // every source can be updated: non-emeras DENMs open the traces-only mode
      onUpdate={onUpdateDenm ? () => onUpdateDenm(event.id) : undefined}
      onClone={
        event.source === DenmSourceFilter.EMERAS && onCloneDenm
          ? () => onCloneDenm(event.id)
          : undefined
      }
      writeAllowed={canWriteDenm}
      blockedActionMode={blockedActionMode}
      blockedMessage={writeBlockedMessage}
    >
      <CardDetails denm={event} cause={cause} subCause={subCause} />
    </EventLayout>
  );
};

export default React.memo(DenmLayout);
